import React, { useEffect, useMemo, useRef } from 'react';
import { Select, SelectItem } from '@carbon/react';
import { useEmployeeRules } from 'src/hooks/useEmployeeRules';

interface EmployeeRuleSelectorProps {
  value: string | undefined | null;
  invalid?: boolean;
  onChange: (value: string | null) => void;
}

const EmployeeRuleSelector = React.memo((props: EmployeeRuleSelectorProps) => {
  const { onChange, value } = props;
  const { data: employeeRules } = useEmployeeRules();
  const rules = employeeRules?.data;

  /**
   * Held in a ref so the effect below does NOT depend on `onChange`'s identity.
   *
   * Every caller passes an inline arrow (see `IssueForm`, where it closes over Formik's
   * `setFieldValue`), so `onChange` is a new function on every render. With it in the
   * dependency array the effect ran, called `onChange`, re-rendered the form, got a fresh
   * `onChange`, and ran again — an infinite render loop that pinned the main thread. It was
   * invisible until a restaurant had house rules, because the whole body sat behind a
   * `rules.length` check, and `IssueForm.test.tsx` mocks the rules as an empty array.
   */
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    // Only supply a default when the field is still empty. The previous version re-applied
    // the first rule whenever this effect ran, which also meant opening an existing issue
    // for editing silently replaced its rule with whichever one happened to be first.
    if (!value && rules?.length) {
      const defaultEmployeeRuleId = rules[0]?.id;
      if (defaultEmployeeRuleId) {
        onChangeRef.current(defaultEmployeeRuleId);
      }
    }
  }, [rules, value]);

  const employeeRulesSelectionItems = useMemo(() => {
    return employeeRules?.data?.map((item) => ({ id: item.id, text: item.title }));
  }, [employeeRules?.data]);

  // Double-negated on purpose: the bare `&&` handed Carbon the array LENGTH, which warns
  // "Invalid prop `invalid` of type `number` supplied to `Select`, expected `boolean`".
  const hasRulesButNoSelection = !value && !!employeeRulesSelectionItems?.length;

  return (
    <div className={'cds--fullwidth'}>
      <label className={'cds--label'} htmlFor={'employee-rule-selector'}>
        Select rule
      </label>
      <Select
        labelText="Select an option"
        hideLabel
        id={'employee-rule-selector'}
        onChange={(e: any) => props.onChange(e.target.value)}
        invalid={hasRulesButNoSelection}
        value={props.value ?? ''}
      >
        {employeeRulesSelectionItems?.map((item) => {
          return <SelectItem value={item.id} key={item.id} text={item.text} />;
        })}
      </Select>
    </div>
  );
});

export default EmployeeRuleSelector;
